"use client";

import type { DiscoverSelection } from "@/components/discover/filters/selection";
import type { LocaleType } from "@/constants/locales";
import { useIsEmbeddedInApp } from "@/contexts/EmbeddedInAppContext";
import { useSearchTriggerContext } from "@/contexts/SearchTriggerContext";
import { useIsBelowBreakpoint, useIsMobile } from "@/hooks/breakpoints";
import { useAutoHidingHeader } from "@/hooks/useAutoHidingHeader";
import { useSearchLocationFromUrl } from "@/hooks/useSearchLocationFromUrl";
import { useSearchStateManager } from "@/hooks/useSearchStateManager";
import { track } from "@/lib/analytics/client";
import type { Coords } from "@/types/places";
import {
  type CustomLocation,
  getLocaleCountry,
  getLocaleOrganizerMode,
  type MapAreaId,
} from "@/utils/location";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { DesktopSearchBar } from "./DesktopSearchBar";
import { OrganizerCtaPanel } from "./discover/OrganizerCtaPanel";
import { MobileSearchBar } from "./MobileSearchBar";
import { RegionSelector } from "./RegionSelector";
import { Button } from "./ui/button";

type SiteHeaderProps = {
  locale: string;
  children?: React.ReactNode;
  showSearch?: boolean;
  searchProps?: {
    currentArea: MapAreaId;
    currentCustomLocation?: CustomLocation | null;
    coords: Coords;
    selection: DiscoverSelection;
    redirectToHome?: boolean;
    /**
     * Take the search location from `?lat&lon&locationName` instead of the
     * values above. Set on the cached detail routes, whose server render cannot
     * see search params. Listing pages resolve their own location and leave it
     * off.
     */
    resolveLocationFromUrl?: boolean;
  };
};

/**
 * Inside the Spond mobile app web view the top nav row (logo + List My Club +
 * region selector) is hidden so only the MobileSearchBar remains. The hiding is
 * done in CSS, keyed on the attribute set before paint; `useIsEmbeddedInApp()`
 * only disables the scroll-driven auto-hide, which nothing visible waits on.
 */
export default function SiteHeader({
  locale,
  children,
  showSearch = false,
  searchProps,
}: SiteHeaderProps) {
  const t = useTranslations();
  const homeLink = useSearchStateManager(locale);
  const embeddedInApp = useIsEmbeddedInApp();
  const urlLocation = useSearchLocationFromUrl();

  const resolvedSearchProps = searchProps?.resolveLocationFromUrl
    ? { ...searchProps, currentCustomLocation: null, ...urlLocation }
    : searchProps;
  const shouldUseAutoHiding = useIsBelowBreakpoint("md") && !embeddedInApp;
  const isMobile = useIsMobile();
  const { isAnyTriggerOpen } = useSearchTriggerContext();
  const [organizerCtaOpen, setOrganizerCtaOpen] = useState(false);
  const organizerMode = getLocaleOrganizerMode(locale as LocaleType);
  const ctaLabel =
    organizerMode === "form"
      ? t("siteHeader.listMyClub")
      : t("siteHeader.listYourEvent");

  const isDesktopExpanded =
    isAnyTriggerOpen && !shouldUseAutoHiding && !embeddedInApp;
  const [navBarHeight, setNavBarHeight] = useState(96);
  const headerRef = useRef<HTMLElement>(null);
  const [navBar, setNavBar] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!navBar) return;

    // Inside the app web view the row is `display: none`, so it measures 0.
    // Keep the last real height rather than collapsing the expanded margin.
    const updateNavBarHeight = () => {
      const height = navBar.clientHeight;
      if (height > 0) setNavBarHeight(height);
    };

    updateNavBarHeight();

    window.addEventListener("resize", updateNavBarHeight);
    return () => {
      window.removeEventListener("resize", updateNavBarHeight);
    };
  }, [navBar]);

  // Keep --header-height CSS variable in sync with the actual rendered height.
  // Set on documentElement (not the header itself) so sibling elements like
  // SearchOverlay can read it via normal CSS custom property inheritance.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const root = document.documentElement;
    const syncCssVar = () => {
      root.style.setProperty("--header-height", `${el.offsetHeight}px`);
    };

    const ro = new ResizeObserver(syncCssVar);
    ro.observe(el);
    syncCssVar();

    return () => {
      ro.disconnect();
      root.style.removeProperty("--header-height");
    };
  }, []);

  useAutoHidingHeader({
    ref: headerRef,
    enabled: shouldUseAutoHiding,
    hiddenOffset: navBarHeight,
  });

  return (
    <header
      ref={headerRef}
      className="sticky z-50 w-full transition-[transform,margin-bottom] duration-300 ease-out top-0"
      id="site-header"
      // While auto-hiding, marginBottom belongs to the scroll controller: a
      // React re-render writing it would drop the collapse mid-scroll and leave
      // a gap under the search bar.
      style={
        shouldUseAutoHiding
          ? undefined
          : { marginBottom: isDesktopExpanded ? -(navBarHeight - 64) : 0 }
      }
    >
      <div
        data-testid="top-nav"
        className="hide-when-embedded-in-app bg-white transition-[height,background-color,padding,border-color] duration-300 ease-out pt-3 pb-0 md:py-5 md:border border-border"
        ref={setNavBar}
      >
        <div className="flex items-center justify-between gap-3 max-w-content mx-auto px-4 sm:px-6 h-10 md:h-full">
          <Link
            href={homeLink}
            aria-label={t("logo.homeLink")}
            className="inline-flex items-center"
          >
            <Image
              src="/spond-logo-new.svg"
              alt={t("logo.alt")}
              width={109}
              height={24}
              className="h-8 w-auto min-w-0 object-contain object-left"
              priority
              fetchPriority="high"
            />
          </Link>

          {showSearch && resolvedSearchProps ? (
            <>
              <div className="hidden md:flex">
                <DesktopSearchBar
                  currentArea={resolvedSearchProps.currentArea}
                  currentCustomLocation={
                    resolvedSearchProps.currentCustomLocation
                  }
                  coords={resolvedSearchProps.coords}
                  selection={resolvedSearchProps.selection}
                  redirectToHome={resolvedSearchProps.redirectToHome}
                />
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <div className="not-xs:hidden">
                  <RegionSelector currentLocale={locale as LocaleType} />
                </div>
                <Button
                  className="h-8 xs:h-10 rounded-full px-3 xs:px-4 text-xs xs:text-sm"
                  onClick={() => {
                    track("Click list my club call to action", {
                      location: "site_header",
                      market: getLocaleCountry(locale as LocaleType),
                      mode: organizerMode,
                    });
                    setOrganizerCtaOpen(true);
                  }}
                >
                  <span className="sm:hidden">+ {ctaLabel}</span>
                  <span className="hidden sm:inline">{ctaLabel}</span>
                </Button>
                <OrganizerCtaPanel
                  mode={organizerMode}
                  container={isMobile ? "sheet" : "dialog"}
                  location="site_header"
                  open={organizerCtaOpen}
                  onOpenChange={setOrganizerCtaOpen}
                />
              </div>
            </>
          ) : (
            children
          )}
        </div>
      </div>

      {resolvedSearchProps && (
        <MobileSearchBar
          currentArea={resolvedSearchProps.currentArea}
          currentCustomLocation={resolvedSearchProps.currentCustomLocation}
          coords={resolvedSearchProps.coords}
          selection={resolvedSearchProps.selection}
          redirectToHome={resolvedSearchProps.redirectToHome}
        />
      )}
    </header>
  );
}
